import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, MessageSquare, Search } from 'lucide-react';
import axios from 'axios';
import AdminBottomNav from '../../components/AdminBottomNav';
import NotificationBellButton from '../../components/NotificationBellButton';
import { MessengerListShell } from '../../components/messaging/MessengerListShell';
import { MessengerListRow } from '../../components/messaging/MessengerListRow';
import { buildMessageHeaders } from '../../utils/messageHeaders';
import { formatChatDisplayName } from '../../utils/nameFormatters';
import VenueEmptyState from '../../components/venue/VenueEmptyState';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminMessagesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [filterOpen, setFilterOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = useMemo(() => localStorage.getItem('token'), []);
  const activeVenueAppId = useMemo(() => localStorage.getItem('activeVenueAppId'), []);

  const fetchConversations = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/messages/conversations`, {
        headers: buildMessageHeaders(token, activeVenueAppId, 'venue'),
      });
      setConversations(Array.isArray(res.data) ? res.data : []);
    } catch {
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [token, activeVenueAppId]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  const filtered = useMemo(() => conversations.filter((c) => {
    const name = c.other_user?.name || c.name || '';
    const matchesSearch = !search || name.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'all' || c.type === filter;
    return matchesSearch && matchesFilter;
  }), [conversations, search, filter]);

  const filterNode = (
    <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end' }}>
      <button onClick={() => setFilterOpen((prev) => !prev)} data-testid="admin-messages-filter-button" className="premium-card-compact premium-card-nested" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', cursor: 'pointer' }}>
        <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, color: 'var(--text)' }}>{filter === 'all' ? 'All messages' : filter === 'customer' ? 'Customers' : 'Members'}</span>
        <ChevronDown size={16} color="var(--text-2)" style={{ transform: filterOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 180ms ease' }} />
      </button>
      {filterOpen ? (
        <div className="premium-card-compact premium-card-nested" style={{ position: 'absolute', top: '100%', right: 0, zIndex: 20, minWidth: 150, marginTop: 6, overflow: 'hidden' }}>
          {[
            ['all', 'All messages'],
            ['customer', 'Customers'],
            ['member', 'Members'],
          ].map(([value, label]) => (
            <button key={value} data-testid={`admin-messages-filter-${value}`} onClick={() => { setFilter(value); setFilterOpen(false); }} style={{ width: '100%', textAlign: 'left', padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: "'Inria Serif', serif", fontSize: 15, color: filter === value ? 'var(--accent-purple)' : 'var(--text-2)' }}>{label}</button>
          ))}
        </div>
      ) : null}
    </div>
  );

  return (
    <MessengerListShell
      pageTestId="admin-messages-page"
      bellNode={<NotificationBellButton buttonTestId="admin-messages-bell" popupTestId="admin-messages-bell-popup" />}
      bottomNav={<AdminBottomNav />}
      title="Messages"
      searchValue={search}
      onSearchChange={(event) => setSearch(event.target.value)}
      searchPlaceholder="Guest or member name"
      searchTestId="admin-messages-search-input"
      searchIcon={<Search size={18} color="var(--text-3)" />}
      cardHeader={filterNode}
    >
      {!loading && filtered.length === 0 ? (
        <VenueEmptyState
          icon={MessageSquare}
          title="Your first guest message will appear here"
          description="When a guest opens a chat with the venue, you'll see it light up right in this list."
          testId="admin-messages-empty-state"
          compact
        />
      ) : null}
      {filtered.map((conversation) => {
        const name = conversation.other_user?.name || conversation.name || 'Guest';
        const preview = conversation.last_message?.content || conversation.preview || '';
        const unread = conversation.unread_count || 0;
        return (
          <MessengerListRow
            key={conversation.id}
            name={formatChatDisplayName(name) || name}
            preview={preview}
            unreadCount={unread}
            timestamp={conversation.last_message_at}
            imageUrl={conversation.other_user?.image_url || conversation.other_user?.photo_url}
            dataTestId={`admin-msg-${conversation.id}`}
            onClick={() => navigate(`/staff-admin/chat/${conversation.id}`)}
          />
        );
      })}
    </MessengerListShell>
  );
}
